import { useCallback, useRef } from 'react';

/**
 * Hook pour permettre le défilement au clic glissé (click & drag to scroll) à la souris
 * sur PC / desktop, simulant fidèlement le comportement tactile mobile sur TOUT élément,
 * y compris les modales conditionnelles, tuiles et listes.
 * 
 * @param {Object} options
 * @param {'vertical' | 'horizontal' | 'both'} [options.direction='both']
 * @param {number} [options.speed=1.3]
 * @returns {Function & { current: HTMLElement | null }}
 */
export const useDragToScroll = ({ direction = 'both', speed = 1.3 } = {}) => {
  const cleanupRef = useRef(null);
  const elementRef = useRef(null);

  const refCallback = useCallback((node) => {
    // Nettoyer les anciens écouteurs si le nœud change ou est démonté
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    elementRef.current = node;
    if (!node) return;

    let isDown = false;
    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let scrollTop = 0;
    let hasDragged = false;

    const onMouseDown = (e) => {
      // Ignorer clic droit / milieu
      if (e.button !== 0) return;

      // Ne pas bloquer la saisie dans les vrais champs de texte
      if (e.target.closest('input, textarea, select')) {
        return;
      }

      isDown = true;
      hasDragged = false;
      startX = e.clientX;
      startY = e.clientY;
      scrollLeft = node.scrollLeft;
      scrollTop = node.scrollTop;
    };

    const onMouseMove = (e) => {
      if (!isDown) return;

      const deltaX = (e.clientX - startX) * speed;
      const deltaY = (e.clientY - startY) * speed;

      // Dès qu'on dépasse 4px de mouvement, activer le mode Drag et scroller
      if (!hasDragged && (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4)) {
        hasDragged = true;
        node.style.cursor = 'grabbing';
        node.style.userSelect = 'none';
      }

      if (hasDragged) {
        if (direction === 'both' || direction === 'horizontal') {
          node.scrollLeft = scrollLeft - deltaX;
        }
        if (direction === 'both' || direction === 'vertical') {
          node.scrollTop = scrollTop - deltaY;
        }
      }
    };

    const onMouseUp = () => {
      if (!isDown) return;
      isDown = false;
      if (node) {
        node.style.cursor = '';
        node.style.userSelect = '';
      }
      // Bloquer le clic consécutif si on a glissé
      setTimeout(() => {
        hasDragged = false;
      }, 80);
    };

    const onClickCapture = (e) => {
      if (hasDragged) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    node.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove, { passive: false });
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('click', onClickCapture, true);

    cleanupRef.current = () => {
      node.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('click', onClickCapture, true);
    };
  }, [direction, speed]);

  // Permet à la fois de l'assigner à ref={myRef} et d'accéder à myRef.current
  Object.defineProperty(refCallback, 'current', {
    get: () => elementRef.current,
    set: (val) => { elementRef.current = val; },
    configurable: true,
  });

  return refCallback;
};

export default useDragToScroll;
